// src/api/brands.js
import axios from 'axios';
import { API_URLS } from './config';
import { applyBrandNameCorrections } from '../utils/brandNameCorrections';
import { resolveEnglishBrandQuery, englishBrandName } from '../utils/brandSearch';

const API_URL = API_URLS.API;

// 브랜드 관련 엔드포인트 정의
export const BRANDS_ENDPOINTS = {
  BRAND_RANKINGS: `${API_URL}/brands/rankings`,
  BRAND_DETAIL: `${API_URL}/brands/details/`,
  BRAND_SEARCH: `${API_URL}/brands/search/`,
};

/**
 * 브랜드 랭킹 목록을 가져옵니다.
 * @param {Object} params - 요청 파라미터
 * @param {number} params.page - 페이지 번호
 * @param {number} params.perPage - 페이지당 항목 수
 * @param {string} params.sortBy - 정렬 기준
 * @param {string} params.sortOrder - 정렬 방향 (asc/desc)
 * @param {string} params.category - 카테고리 코드
 * @param {string} params.lang - 응답 언어 (ko/en)
 * @returns {Promise<Object>} 브랜드 랭킹 목록
 */
export const getBrandRankings = async (params) => {
  try {
    const queryParams = new URLSearchParams({
      page: params.page.toString(),
      per_page: params.perPage.toString(),
      sort_by: params.sortBy,
      sort_order: params.sortOrder,
      lang: params.lang || 'ko', // Add language parameter with default
    });

    if (params.category) {
      queryParams.append('category', params.category);
    }

    console.log(`🔍 API Call: ${BRANDS_ENDPOINTS.BRAND_RANKINGS}?${queryParams}`); // Debug log

    const response = await axios.get(`${BRANDS_ENDPOINTS.BRAND_RANKINGS}?${queryParams}`);
    return applyBrandNameCorrections(response.data, params.lang || 'ko');
  } catch (error) {
    console.error('브랜드 랭킹 조회 실패:', error);
    throw error;
  }
};

/**
 * 특정 브랜드의 상세 정보를 가져옵니다.
 * @param {string} brandId - 브랜드 ID
 * @param {string} lang - 응답 언어 (ko/en)
 * @returns {Promise<Object>} 브랜드 상세 정보
 */
export const getBrandDetail = async (brandId, lang = 'ko') => {
  try {
    const queryParams = new URLSearchParams({
      lang: lang
    });

    console.log(`🔍 API Call: ${BRANDS_ENDPOINTS.BRAND_DETAIL}${brandId}?${queryParams}`); // Debug log

    const response = await axios.get(`${BRANDS_ENDPOINTS.BRAND_DETAIL}${brandId}?${queryParams}`);
    return applyBrandNameCorrections(response.data, lang);
  } catch (error) {
    console.error(`브랜드 상세 정보 조회 실패 (ID: ${brandId}):`, error);
    throw error;
  }
};

/**
 * 브랜드를 검색합니다.
 * @param {string} query - 검색어
 * @param {Object} params - 추가 검색 파라미터
 * @param {string} params.lang - 응답 언어 (ko/en)
 * @returns {Promise<Object>} 검색 결과
 */
export const searchBrands = async (query, params = {}) => {
  try {
    const lang = params.lang || 'ko';
    const queryParams = new URLSearchParams({
      lang: lang,
      ...params
    });

    const searchOne = async (term) => {
      const url = `${BRANDS_ENDPOINTS.BRAND_SEARCH}${encodeURIComponent(term)}`;
      const response = await axios.get(`${url}?${queryParams}`);
      return response.data;
    };

    let data = await searchOne(query);

    // The backend only matches Korean brand names, so English queries come
    // back empty — resolve them to Korean names locally and retry.
    if (!data?.items?.length) {
      const candidates = resolveEnglishBrandQuery(query);
      if (candidates.length) {
        const results = await Promise.all(
          candidates.map((c) => searchOne(c.ko).catch(() => null))
        );
        const seen = new Set();
        const items = [];
        results.filter(Boolean).forEach((r) => {
          (r.items || []).forEach((item) => {
            if (!seen.has(item.brand_id)) {
              seen.add(item.brand_id);
              items.push(item);
            }
          });
        });
        if (items.length) {
          data = {
            ...data,
            items,
            total: items.length,
            page: 1,
            total_pages: 1,
            has_next: false,
            has_prev: false,
          };
        }
      }
    }

    // The search endpoint ignores lang=en for names and categories —
    // map results to official English names and translated categories.
    if (lang === 'en' && data?.items) {
      const CATEGORY_EN = {
        '썬': 'Sun', '선': 'Sun', '스킨': 'Skin', '클렌징': 'Cleansing',
        '바디': 'Body', '헤어': 'Hair',
      };
      data.items.forEach((item) => {
        item.brand_name = englishBrandName(item.brand_id) || item.brand_name;
        if (Array.isArray(item.categories)) {
          item.categories = item.categories.map((c) => CATEGORY_EN[c] || c);
        }
      });
    }

    return applyBrandNameCorrections(data, lang);
  } catch (error) {
    console.error(`브랜드 검색 실패 (검색어: ${query}):`, error);
    throw error;
  }
};

const brandsAPI = {
  getBrandRankings,
  getBrandDetail,
  searchBrands,
  BRANDS_ENDPOINTS
};

export default brandsAPI;